import type {
  Testimonial,
  TestimonialStyle,
  TestimonialsProps,
} from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { ColorField, NumberField, SelectField, TextField } from "./fields.js";
import { MediaField } from "./MediaField.js";
import type { WidgetInspectorProps } from "./registry.js";

const COLS = ["1", "2", "3"] as const;
const STYLE = ["card", "minimal"] as const;

function newItemId(): string {
  return `tm-${Math.random().toString(36).slice(2, 8)}`;
}

export function TestimonialsInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as TestimonialsProps;
  const items = p.items ?? [];

  const setItems = (next: Testimonial[]) => update(widget.id, { items: next });

  const addItem = () =>
    setItems([
      ...items,
      {
        id: newItemId(),
        quote: "A great tool.",
        authorName: "Jane Doe",
        authorRole: "CEO, Somewhere",
        avatarUrl: "https://placehold.co/96x96/e5e7eb/6b7280?text=J",
        rating: 5,
      },
    ]);

  const updateItem = (id: string, patch: Partial<Testimonial>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));

  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  return (
    <>
      <SelectField
        label="Columns"
        value={String(p.columns ?? 3) as (typeof COLS)[number]}
        options={COLS}
        onChange={(v) =>
          update(widget.id, {
            columns: Number.parseInt(v, 10) as TestimonialsProps["columns"],
          })
        }
      />
      <SelectField
        label="Style"
        value={p.style ?? "card"}
        options={STYLE}
        onChange={(v) => update(widget.id, { style: v as TestimonialStyle })}
      />
      <ColorField
        label="Card background"
        value={p.cardBackground ?? "#ffffff"}
        onChange={(v) => update(widget.id, { cardBackground: v })}
      />
      <ColorField
        label="Text"
        value={p.textColor ?? "#0f172a"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />
      <ColorField
        label="Accent (stars)"
        value={p.accent ?? "#f59e0b"}
        onChange={(v) => update(widget.id, { accent: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Testimonials ({items.length})
      </div>

      {items.map((it, i) => (
        <div
          key={it.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "down")}
              disabled={i === items.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === items.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeItem(it.id)}
              title="Delete"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>

          <TextField
            label={`Quote ${i + 1}`}
            value={it.quote}
            multiline
            onChange={(v) => updateItem(it.id, { quote: v })}
          />
          <TextField
            label="Author name"
            value={it.authorName}
            onChange={(v) => updateItem(it.id, { authorName: v })}
          />
          <TextField
            label="Author role"
            value={it.authorRole ?? ""}
            onChange={(v) => updateItem(it.id, { authorRole: v || undefined })}
          />
          <MediaField
            label="Avatar URL"
            value={it.avatarUrl ?? ""}
            onChange={(v) => updateItem(it.id, { avatarUrl: v || undefined })}
          />
          <NumberField
            label="Rating (0-5)"
            value={it.rating ?? 0}
            min={0}
            max={5}
            onChange={(v) => updateItem(it.id, { rating: v })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add testimonial
        </button>
      </div>
    </>
  );
}
